/**
 * RFC-239 — Auditor Center → Populations, as three routes:
 *
 *   /populations                                  list: freeze per class + every freeze as a row
 *   /populations/$populationId                    detail: rows, controlled view, sample selection, rounds
 *   /populations/$populationId/rounds/$sampleId   one append-only sampling round with its frozen evidence
 *
 * Workspace admins see the same auditor-facing pages; the
 * server guard (resolveAuditorCenterCycleAccess) is shared with the auditor seat.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod/v3'
import { format, formatDistanceToNow } from 'date-fns'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ArrowLeft, ArrowRight, Download, Loader2, Snowflake } from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { downloadBase64File } from '@/utils/download'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@deepguide-ai/ui/sheet'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import { Input } from '@deepguide-ai/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@deepguide-ai/ui/table'
import { AuditorInlineEmptyState } from './auditor-center-shared'
import { PopulationFilterBar } from '@/components/compliance/PopulationFilterBar'
import type { CyclePopulationClass, PopulationDTO, ExclusionRule, RiskSamplingSnapshotDTO } from '@deepguide-ai/types'

const CLASSES: Array<{ key: CyclePopulationClass; label: string; source: string }> = [
  { key: 'people', label: 'People', source: 'Employees and contractors active in the window' },
  { key: 'devices', label: 'Devices', source: 'Devices linked to people in the window' },
  { key: 'changes', label: 'Changes', source: 'Pull requests merged into the default branch in the window' },
  { key: 'assets', label: 'Assets', source: 'Asset register entries in scope during the window' },
  { key: 'risks', label: 'Risks', source: 'Valid risk register entries as of freeze time, including closed risks' },
]
const CLASS_LABEL = Object.fromEntries(CLASSES.map((c) => [c.key, c.label])) as Record<CyclePopulationClass, string>
const LIST_ROUTE = '/workspaces/$workspaceId/auditor-center/$auditCycleId/populations' as const
const DETAIL_ROUTE = '/workspaces/$workspaceId/auditor-center/$auditCycleId/populations/$populationId' as const
const ROUND_ROUTE = '/workspaces/$workspaceId/auditor-center/$auditCycleId/populations/$populationId/rounds/$sampleId' as const
/** `forTest`: the test the auditor came from — a round saved here is applied to it. */
export const auditorPopulationSearchSchema = z.object({
  forTest: z.string().optional().catch(undefined),
})
const fmt = (d: string | Date | null | undefined) => (d ? format(new Date(d), 'PP') : '—')
const fmtTime = (d: string | Date | null | undefined) => (d ? format(new Date(d), 'PPp') : '—')

type PopulationSummary = Pick<PopulationDTO, 'id' | 'name' | 'recordCount' | 'headers' | 'auditorHiddenColumns' | 'interim' | 'fileHash' | 'periodStart' | 'periodEnd'> & {
  asOfDate: Date | string
  class: CyclePopulationClass
  excludedCount: number
  daysToPeriodEnd: number | null
  roundCount: number
  frozenByName: string | null
}

/** Viewing saved facts never reads providers or current risk records. */
function PopulationEvidenceButton({ kind, row, snapshot, at }: { kind: 'change' | 'risk'; row: unknown; snapshot: unknown; at: Date | string | null }) {
  const [open, setOpen] = useState(false)
  const risk = kind === 'risk' ? snapshot as RiskSamplingSnapshotDTO | null : null
  const record = kind === 'risk' ? risk?.risk ?? {} : (snapshot as { change?: Record<string, unknown> } | null)?.change ?? (row as Record<string, unknown>)
  return <>
    <Button size='sm' variant='outline' onClick={() => setOpen(true)}>View details</Button>
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent>
        <SheetHeader><SheetTitle>{kind === 'risk' ? 'Risk evidence' : 'Change evidence'}</SheetTitle><SheetDescription>
          {kind === 'risk'
            ? `Facts captured ${fmtTime(at)}. Current snapshot, not audit-period history.`
            : at ? `Saved evidence · ${fmtTime(at)}` : 'Frozen PR record. Evidence is collected when this round is applied to a test.'}
        </SheetDescription></SheetHeader>
        <ScrollArea className='min-h-0 flex-1' viewClassName='[&>div]:!block'>
          <div className='space-y-3 px-6 py-4 text-sm'>
            {kind === 'risk' && <p className='text-muted-foreground'>Human review required. A plan, closed status or link does not prove execution. Referenced document contents are not captured. Re-freeze and select a new round to include later changes.</p>}
            {risk?.restricted ? <p>Evidence withheld by the client’s population column restrictions.</p> : <dl className='space-y-3'>
              {Object.entries(record).filter(([key]) => !key.startsWith('__') && key !== 'providerExtensions').map(([key, value]) => <div key={key}>
                <dt className='text-muted-foreground'>{key}</dt>
                <dd className='whitespace-pre-wrap break-words'>{value == null ? 'Not collected / unavailable' : kind === 'risk' && typeof value === 'object' ? JSON.stringify(value, null, 2) : Array.isArray(value) ? value.join(', ') || 'None' : String(value)}</dd>
              </div>)}
            </dl>}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  </>
}

function StateBadge({ population }: { population: PopulationSummary | undefined }) {
  if (!population) return <Badge variant='outline'>not frozen</Badge>
  if (population.class === 'risks') return <Badge variant='outline'>point-in-time snapshot</Badge>
  return (
    <Badge variant='outline' className={population.interim ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-green-200 bg-green-50 text-green-700'}>
      {population.interim ? `interim · ${population.daysToPeriodEnd}d left` : 'final'}
    </Badge>
  )
}

type Snapshot =
  | RiskSamplingSnapshotDTO
  | { kind: 'person'; employee: { status?: string; backgroundCheckStatus?: string | null; ndaSignedAt?: string | null } | null; training: { assigned: number; completed: number } | null; devices: { total: number; compliant: number } | null; acknowledgments: { assigned: number; completed: number } | null }
  | { kind: 'device'; device: { complianceStatus?: string; encryptionEnabled?: boolean | null; antivirusEnabled?: boolean | null; lastCheckIn?: string | null } | null }
  | { kind: 'change'; observationId?: string | null; change?: Record<string, unknown> }

type Fact = { key: string; value: string; ok: boolean | null }

/** One row of facts per snapshot; rendered as chips on the round page and as columns in the round CSV. */
function snapshotFacts(snapshot: unknown): Fact[] {
  const s = snapshot as Snapshot | null
  if (!s) return [{ key: 'evidence', value: 'Not collected', ok: null }]
  if (s.kind === 'person') {
    const t = s.training, d = s.devices, a = s.acknowledgments
    const bg = s.employee?.backgroundCheckStatus ?? null
    return [
      ...(t ? [{ key: 'training', value: `${t.completed}/${t.assigned}`, ok: t.completed >= t.assigned }] : []),
      ...(d ? [{ key: 'devices', value: `${d.compliant}/${d.total} compliant`, ok: d.compliant >= d.total }] : []),
      ...(a ? [{ key: 'acks', value: `${a.completed}/${a.assigned}`, ok: a.completed >= a.assigned }] : []),
      { key: 'background check', value: bg ?? 'unknown', ok: bg ? /complete|passed|clear/i.test(bg) : null },
      { key: 'agreement', value: s.employee?.ndaSignedAt ? fmt(s.employee.ndaSignedAt) : 'none recorded', ok: s.employee?.ndaSignedAt ? true : null },
    ]
  }
  if (s.kind === 'device') {
    const dv = s.device
    return [
      { key: 'status', value: dv?.complianceStatus ?? 'unknown', ok: dv?.complianceStatus === 'COMPLIANT' },
      { key: 'encryption', value: dv?.encryptionEnabled == null ? 'unknown' : dv.encryptionEnabled ? 'on' : 'off', ok: dv?.encryptionEnabled ?? null },
      { key: 'anti-malware', value: dv?.antivirusEnabled == null ? 'unknown' : dv.antivirusEnabled ? 'on' : 'off', ok: dv?.antivirusEnabled ?? null },
      { key: 'check-in', value: fmt(dv?.lastCheckIn ?? null), ok: null },
    ]
  }
  if (s.kind === 'risk') {
    if (s.restricted) return [{ key: 'evidence', value: 'Withheld by column restrictions', ok: null }]
    return ['treatment', 'status', 'ownerLabel'].filter((key) => s.risk[key] != null).map((key) => ({ key, value: String(s.risk[key]), ok: null }))
  }
  const c = s.change
  if (!c) return [{ key: 'evidence', value: 'Not collected', ok: null }]
  const approvers = c.approvers == null ? 'Unknown' : String(c.approvers)
  return [
    { key: 'approvers', value: approvers, ok: c.approvers == null ? null : Array.isArray(c.approvers) ? c.approvers.length > 0 : approvers !== '[]' },
    { key: 'reviews', value: String(c.reviewCount ?? '?'), ok: null },
    { key: 'merged', value: fmt(String(c.mergedAt ?? '') || null), ok: null },
  ]
}

function SnapshotChips({ snapshot }: { snapshot: unknown }) {
  const facts = snapshotFacts(snapshot)
  if (!facts.length) return <span className='text-xs text-muted-foreground'>no snapshot</span>
  return (
    <span>
      {facts.map((f) => (
        <Badge key={f.key} variant='outline' className={cn('mr-1 text-xs', f.ok === true && 'border-green-200 bg-green-50 text-green-700', f.ok === false && 'border-red-200 bg-red-50 text-red-700')}>
          {f.key} {f.value}
        </Badge>
      ))}
    </span>
  )
}

const csvCell = (v: unknown) => { const s = v == null ? '' : String(v); return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }

/** One row per pick: # + the (already hidden-column-stripped) population row + one column per snapshot fact. */
function roundCsv(items: { rowIndex: number; rowData: unknown; evidenceSnapshot: unknown; evidenceSnapshotAt: Date | string | null }[]): string {
  const rows = items.map((item) => {
    const data = Object.fromEntries(Object.entries((item.rowData ?? {}) as Record<string, unknown>).filter(([k]) => !k.startsWith('__')))
    const facts = Object.fromEntries(snapshotFacts(item.evidenceSnapshot).map((f) => [f.key, f.value]))
    return { '#': item.rowIndex + 1, ...data, ...facts, ...((item.evidenceSnapshot as { kind?: string } | null)?.kind === 'risk' ? { evidenceSnapshot: JSON.stringify(item.evidenceSnapshot) } : {}), snapshotAt: item.evidenceSnapshotAt ? new Date(item.evidenceSnapshotAt).toISOString() : '' }
  })
  const headers = [...new Set(rows.flatMap((r) => Object.keys(r)))]
  return [headers.join(','), ...rows.map((r) => headers.map((h) => csvCell((r as Record<string, unknown>)[h])).join(','))].join('\n')
}

function useCycleParams() {
  const { workspaceId, auditCycleId, populationId, sampleId } = useParams({ strict: false })
  const { forTest } = useSearch({ strict: false }) as { forTest?: string }
  return { workspaceId: workspaceId!, auditCycleId: auditCycleId!, populationId, sampleId, forTest }
}

type ApplyResult = { outcome: 'existing' | 'parked' }

/** One line for what applying a round did, per outcome. */
function applySummary(results: ApplyResult[] | null): string {
  if (results === null) return 'Collecting sample evidence in the background. Submissions will appear when complete.'
  const count = (outcome: ApplyResult['outcome']) => results.filter((r) => r.outcome === outcome).length
  return [
    count('parked') && `${count('parked')} sent to the client to approve`,
    count('existing') && `${count('existing')} already had this round`,
  ].filter(Boolean).join(' · ')
}

/** Everything that shows a test's sampling evidence. */
function useInvalidateSamplingEvidence() {
  const queryClient = useQueryClient()
  return useCallback(() => {
    for (const queryKey of [
      trpc.auditor.listSamplingTests.queryKey(),
      trpc.auditor.getTestSampling.queryKey(),
      trpc.auditor.getControlTestSubmissions.queryKey(),
    ]) void queryClient.invalidateQueries({ queryKey })
  }, [queryClient])
}

// ── 1. List ──────────────────────────────────────────────────────────

export function AuditorPopulationsListPage() {
  const { workspaceId, auditCycleId } = useCycleParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const listQuery = useQuery({
    ...trpc.auditor.listCyclePopulations.queryOptions({ auditCycleId }),
    // Changes are collected on the worker: follow the collection until the population lands.
    refetchInterval: (query) => (query.state.data?.changesCollection?.state === 'collecting' ? 5_000 : false),
  })
  const freeze = useMutation({
    ...trpc.auditor.freezeCyclePopulation.mutationOptions(),
    onSuccess: (population) => {
      void queryClient.invalidateQueries({ queryKey: trpc.auditor.listCyclePopulations.queryKey({ auditCycleId }) })
      if (!population) {
        toast.success('Collecting merged pull requests from GitHub. The population appears here when it is done.')
        return
      }
      toast.success(`${CLASS_LABEL[population.class]} frozen as of ${fmt(population.asOfDate)}${population.class !== 'risks' && population.interim ? ' (interim)' : ''}`)
      void navigate({ to: DETAIL_ROUTE, params: { workspaceId, auditCycleId, populationId: population.id } })
    },
    onError: (error) => toast.error(error.message),
  })
  const populations = useMemo(() => listQuery.data?.items ?? [], [listQuery.data])
  const collection = listQuery.data?.changesCollection ?? null
  const collecting = collection?.state === 'collecting'
  const latestByClass = useMemo(() => {
    const map = new Map<string, PopulationSummary>()
    for (const p of populations) if (!map.has(p.class)) map.set(p.class, p)
    return map
  }, [populations])

  if (listQuery.isPending) return <div className='flex items-center gap-2 text-sm text-muted-foreground'><Loader2 className='h-4 w-4 animate-spin' /> Loading populations…</div>

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-xl font-semibold'>Populations</h1>
        <p className='mt-1 text-sm text-muted-foreground'>
          Frozen asset lists for this cycle. Every freeze is kept; open one to see its rows and take a sample. The product never suggests a sample size.
        </p>
      </div>

      <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-4'>
        {CLASSES.map(({ key, label, source }) => {
          const latest = latestByClass.get(key)
          return (
            <div key={key} className='rounded-md border bg-card px-4 py-3 text-sm'>
              <div className='flex items-center justify-between'>
                <span className='font-semibold'>{label}</span>
                <StateBadge population={latest} />
              </div>
              <p className='mt-1 text-xs text-muted-foreground'>{source}</p>
              <p className='mt-2 text-xs text-muted-foreground'>
                {latest ? <>Latest: <span className='font-medium text-foreground tabular-nums'>{latest.recordCount} rows</span> as of {fmt(latest.asOfDate)}</> : 'Not frozen yet'}
              </p>
              {key === 'changes' && collection?.state === 'collecting' && (
                <p className='mt-1 flex items-center gap-1 text-xs text-muted-foreground'>
                  <Loader2 className='h-3 w-3 animate-spin' /> Collecting from GitHub, started {formatDistanceToNow(new Date(collection.since), { addSuffix: true })}
                </p>
              )}
              {key === 'changes' && collection?.state === 'failed' && (
                <p className='mt-1 text-xs text-destructive'>Last collection failed: {collection.error}</p>
              )}
              <div className='mt-3 flex flex-wrap gap-2'>
                {latest && (
                  <Button size='sm' variant='outline' asChild>
                    <Link to={DETAIL_ROUTE} params={{ workspaceId, auditCycleId, populationId: latest.id }}>Open latest</Link>
                  </Button>
                )}
                <Button size='sm' variant={latest ? 'outline' : 'default'} disabled={freeze.isPending || (key === 'changes' && collecting)} onClick={() => freeze.mutate({ auditCycleId, class: key })}>
                  {freeze.isPending && freeze.variables?.class === key ? <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' /> : <Snowflake className='mr-1 h-3.5 w-3.5' />}
                  {latest ? 'Re-freeze' : 'Freeze as of now'}
                </Button>
              </div>
            </div>
          )
        })}
      </div>

      <div>
        <h2 className='mb-2 text-sm font-semibold'>All freezes</h2>
        {populations.length === 0 ? (
          <AuditorInlineEmptyState message='Nothing frozen yet. Freeze a population to see its rows and take a sample.' />
        ) : (
          <div className='overflow-x-auto rounded-md border bg-card'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Class</TableHead>
                  <TableHead>As of</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead className='text-right'>Rows</TableHead>
                  <TableHead className='text-right'>Rounds</TableHead>
                  <TableHead>Frozen by</TableHead>
                  <TableHead>sha256</TableHead>
                  <TableHead className='w-0' />
                </TableRow>
              </TableHeader>
              <TableBody>
                {populations.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className='font-medium'>
                      <Link to={DETAIL_ROUTE} params={{ workspaceId, auditCycleId, populationId: p.id }} className='text-accent-foreground underline-offset-2 hover:underline'>
                        {CLASS_LABEL[p.class]}
                      </Link>
                    </TableCell>
                    <TableCell>{fmtTime(p.asOfDate)}</TableCell>
                    <TableCell><StateBadge population={p} /></TableCell>
                    <TableCell className='text-right tabular-nums'>{p.recordCount}</TableCell>
                    <TableCell className='text-right tabular-nums'>{p.roundCount}</TableCell>
                    <TableCell className='text-xs'>{p.frozenByName ?? '—'}</TableCell>
                    <TableCell className='font-mono text-xs text-muted-foreground'>{p.fileHash?.slice(0, 12) ?? '—'}</TableCell>
                    <TableCell className='text-right'>
                      <Button size='sm' variant='outline' asChild>
                        <Link to={DETAIL_ROUTE} params={{ workspaceId, auditCycleId, populationId: p.id }} aria-label={`View ${CLASS_LABEL[p.class]} freeze of ${fmtTime(p.asOfDate)}`}>View</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  )
}

// ── 2. Detail ────────────────────────────────────────────────────────

/** "1,3,5-8" (1-based, as printed in the # column) → 0-based positions; out-of-range and junk tokens are dropped. */
function parseRowNumbers(text: string, recordCount: number): Set<number> {
  const out = new Set<number>()
  for (const token of text.split(/[,\s]+/)) {
    const m = /^(\d+)(?:-(\d+))?$/.exec(token)
    if (!m) continue
    const lo = Number(m[1]); const hi = m[2] ? Number(m[2]) : lo
    for (let n = lo; n <= hi && n <= recordCount; n++) if (n >= 1) out.add(n - 1)
  }
  return out
}
const formatRowNumbers = (picked: Set<number>) => [...picked].sort((a, b) => a - b).map((i) => i + 1).join(',')

export function AuditorPopulationDetailPage() {
  const { workspaceId, auditCycleId, populationId, forTest } = useCycleParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const invalidateSamplingEvidence = useInvalidateSamplingEvidence()
  const listQuery = useQuery(trpc.auditor.listCyclePopulations.queryOptions({ auditCycleId }))
  const population = listQuery.data?.items.find((p) => p.id === populationId) as PopulationSummary | undefined
  const testsQuery = useQuery({
    ...trpc.auditor.listSamplingTests.queryOptions({ auditCycleId, populationId: populationId! }),
    enabled: Boolean(forTest),
  })
  const forTestInfo = forTest ? testsQuery.data?.find((t) => t.statusId === forTest) : undefined
  const applyRound = useMutation(trpc.auditor.applyRoundToTests.mutationOptions())
  const [rowCursor, setRowCursor] = useState(0)
  const [filters, setFilters] = useState<ExclusionRule[]>([])
  const rowsQuery = useQuery(trpc.auditor.getCyclePopulationRows.queryOptions({ auditCycleId, populationId: populationId!, cursor: rowCursor, limit: 100, filters }))
  const roundsQuery = useQuery(trpc.auditor.listSamplingRounds.queryOptions({ auditCycleId, populationId: populationId! }))
  const [method, setMethod] = useState<'JUDGMENTAL' | 'RANDOM' | 'ALL'>('JUDGMENTAL')
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const [rowsText, setRowsText] = useState('')
  const eligibleIndices = rowsQuery.data?.eligibleIndices ?? []
  const pick = (next: Set<number>) => {
    setPicked(next)
    setRowsText(formatRowNumbers(new Set([...next].map((i) => eligibleIndices.indexOf(i)).filter((i) => i >= 0))))
  }
  const [size, setSize] = useState('5')
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 1_000_000))
  const [notes, setNotes] = useState('')
  const changeFilters = (next: ExclusionRule[]) => { setFilters(next); setRowCursor(0); pick(new Set()) }
  useEffect(() => { setFilters([]); setRowCursor(0); setPicked(new Set()); setRowsText('') }, [populationId])

  const invalidateList = () => void queryClient.invalidateQueries({ queryKey: trpc.auditor.listCyclePopulations.queryKey({ auditCycleId }) })
  const download = useMutation({
    ...trpc.auditor.downloadCyclePopulation.mutationOptions(),
    onSuccess: (file) => { downloadBase64File(file.data, file.filename, file.contentType); toast.success(`Downloaded · sha256 ${file.sha256.slice(0, 12)}…`) },
    onError: (error) => toast.error(error.message),
  })
  const createRound = useMutation({
    ...trpc.auditor.createSamplingRound.mutationOptions(),
    onSuccess: (round) => {
      toast.success(`Round ${round.round} saved · ${round.sampleSize} items`)
      pick(new Set()); setNotes('')
      invalidateList()
      void queryClient.invalidateQueries({ queryKey: trpc.auditor.listSamplingRounds.queryKey({ auditCycleId, populationId: populationId! }) })
      const goToRound = () => void navigate({
        to: ROUND_ROUTE,
        params: { workspaceId, auditCycleId, populationId: populationId!, sampleId: round.id },
        search: forTest ? { forTest } : {},
      })
      if (!forTestInfo) return goToRound()
      applyRound.mutate({ auditCycleId, sampleId: round.id, statusIds: [forTestInfo.statusId] }, {
        onSuccess: (results) => { toast.success(`${forTestInfo.code ?? 'Test'}: ${applySummary(results)}`); invalidateSamplingEvidence() },
        onError: (error) => toast.error(error.message),
        onSettled: goToRound,
      })
    },
    onError: (error) => toast.error(error.message),
  })

  if (listQuery.isPending) return <div className='text-sm text-muted-foreground'>Loading population…</div>
  if (!population) return <AuditorInlineEmptyState message='Population not found for this cycle.' />

  const rows = rowsQuery.data
  // Internal join keys (`__employeeId`, `__deviceId`, `__resourceId`…) stay in the CSV and the API.
  const isChanges = population.class === 'changes'
  const columns = isChanges
    ? [
        { key: 'identifier', label: 'PR' },
        { key: 'title', label: 'Title' },
        { key: 'repository', label: 'Repository' },
        { key: 'author', label: 'Author' },
        { key: 'mergedAt', label: 'Merged at' },
      ]
    : (rows?.headers.filter((h) => !h.startsWith('__')) ?? []).map((key) => ({ key, label: key }))
  const matchingCount = rows?.matchingCount ?? population.recordCount
  const eligible = new Set(eligibleIndices)
  const canAll = matchingCount > 0 && (population.class === 'changes' || population.class === 'risks' || matchingCount <= 25)
  const sampleSize = Number(size)
  const canSave = !rowsQuery.isPending && !rowsQuery.isFetching && !rowsQuery.isError && (method === 'JUDGMENTAL' ? picked.size > 0 && (!filters.length || [...picked].every((i) => eligible.has(i))) : method === 'RANDOM' ? Number.isInteger(sampleSize) && sampleSize > 0 && sampleSize <= matchingCount : canAll)

  return (
    <div className='space-y-4'>
      <Link to={LIST_ROUTE} params={{ workspaceId, auditCycleId }} className='inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'>
        <ArrowLeft className='h-3.5 w-3.5' /> All populations
      </Link>
      {forTestInfo && (
        <div className='rounded-md border border-cobalt-border bg-primary-subtle px-4 py-2 text-xs'>
          Sampling for <span className='font-semibold'>{forTestInfo.code} {forTestInfo.title}</span>.
          Each item must show: {forTestInfo.facetLabel}. Saving a round applies it to this test.
        </div>
      )}
      <div className='flex flex-wrap items-start justify-between gap-2'>
        <div>
          <h1 className='text-xl font-semibold'>{CLASS_LABEL[population.class]} <StateBadge population={population} /></h1>
          <p className='mt-1 text-xs text-muted-foreground'>
            {population.recordCount} rows · frozen {fmtTime(population.asOfDate)} · {population.class === 'risks' ? 'valid risks only; invalidated registrations excluded' : `${population.excludedCount} scope-excluded people`} · sha256 {population.fileHash?.slice(0, 16) ?? '—'}
          </p>
        </div>
        <Button size='sm' variant='outline' disabled={download.isPending} onClick={() => download.mutate({ auditCycleId, populationId: population.id })}>
          <Download className='mr-1 h-3.5 w-3.5' /> CSV
        </Button>
      </div>

      {population.class === 'risks' && <p className='text-sm text-muted-foreground'>Current snapshot only, not a reconstruction of the audit period. Closed risks remain available for sampling. No sampled risks are automatically approved.</p>}
      <div className='rounded-md border bg-card'>
        <PopulationFilterBar fields={rows?.filterFields ?? []} filters={filters} onChange={changeFilters} auditWindowAvailable={Boolean(population.periodStart && population.periodEnd)} suggestedPreset={forTestInfo?.facet === 'person_onboarded' ? 'hiredInWindow' : forTestInfo?.facet === 'person_offboarded' ? 'leftInWindow' : undefined} />
        <p className='px-4 pb-3 text-xs text-muted-foreground'>Full population: {population.recordCount} · Matches filters: {matchingCount} · Selected: {method === 'ALL' ? matchingCount : method === 'RANDOM' ? sampleSize : picked.size}</p>
        <div className='flex flex-wrap items-center gap-3 px-4 pb-3 text-sm'>
          <div className='inline-flex h-8 overflow-hidden rounded-sm border'>
            {(['JUDGMENTAL', 'RANDOM', 'ALL'] as const).map((m) => (
              <Button key={m} size='sm' variant={method === m ? 'default' : 'ghost'} className='h-full rounded-none' type='button' disabled={m === 'ALL' && !canAll} onClick={() => setMethod(m)}>
                {m === 'JUDGMENTAL' ? 'Judgmental' : m === 'RANDOM' ? 'Random N' : population.class === 'changes' || population.class === 'risks' ? 'All' : 'All (≤25)'}
              </Button>
            ))}
          </div>
          {method === 'RANDOM' && (
            <>
              <span className='text-xs text-muted-foreground'>N</span>
              <Input type='number' min={1} max={matchingCount} value={size} onChange={(e) => setSize(e.target.value)} className='h-8 w-20' />
              <span className='text-xs text-muted-foreground'>seed</span>
              <Input type='number' value={seed} onChange={(e) => setSeed(Number(e.target.value) || 0)} className='h-8 w-28' />
            </>
          )}
          {method === 'JUDGMENTAL' && (
            <>
              <span className='text-xs text-muted-foreground'>{picked.size} of {matchingCount} matching records selected</span>
              <Input placeholder='Rows, e.g. 1,3,5-8' aria-label='Row numbers' value={rowsText}
                onChange={(e) => { setRowsText(e.target.value); setPicked(new Set([...parseRowNumbers(e.target.value, eligibleIndices.length)].map((i) => eligibleIndices[i]))) }} className='h-8 w-40' />
            </>
          )}
          <Input placeholder='Rationale (optional)' value={notes} onChange={(e) => setNotes(e.target.value)} className='h-8 min-w-[220px] flex-1' />
          <Button size='sm' disabled={!canSave || createRound.isPending}
            onClick={() => createRound.mutate({
              auditCycleId, populationId: population.id, method, notes: notes || undefined, filters,
              selectedIndices: method === 'JUDGMENTAL' ? [...picked].sort((a, b) => a - b) : undefined,
              sampleSize: method === 'RANDOM' ? sampleSize : undefined,
              seed: method === 'RANDOM' ? seed : undefined,
            })}>
            {createRound.isPending ? <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' /> : null}
            {method === 'JUDGMENTAL' && picked.size === 0 ? 'Select at least one row' : 'Save round'}
          </Button>
        </div>
        {method === 'JUDGMENTAL' && filters.length > 0 && [...picked].some((i) => !eligible.has(i)) && <p className='px-4 pb-3 text-xs text-destructive'>Selected row numbers must match the active filters.</p>}
      </div>

      {rowsQuery.isPending ? (
        <div className='text-sm text-muted-foreground'>Loading rows…</div>
      ) : rowsQuery.isError ? (
        <div className='text-sm text-destructive'>Could not load population rows. Clear filters or retry. <Button variant='link' size='sm' onClick={() => rowsQuery.refetch()}>Retry</Button></div>
      ) : rows ? (
        <div className='overflow-x-auto rounded-md border bg-card'>
          {!rows.items.length && <p className='px-3 py-3 text-sm text-muted-foreground'>{rows.totalCount ? 'No records match these filters.' : 'This population has no records.'}</p>}
          <Table>
            <TableHeader>
              <TableRow>
                {method === 'JUDGMENTAL' && <TableHead className='w-8' />}
                <TableHead className='w-12'>#</TableHead>
                {columns.map(({ key, label }) => <TableHead key={key}>{label}</TableHead>)}
                {isChanges ? <TableHead>Details</TableHead> : rows.hiddenColumns.map((h) => <TableHead key={h} className='italic text-muted-foreground'>{h} · hidden by client</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.items.map((row, position) => {
                const idx = row.rowIndex as number
                const on = picked.has(idx)
                return (
                  <TableRow key={idx} data-state={on ? 'selected' : undefined}>
                    {method === 'JUDGMENTAL' && (
                      <TableCell>
                        <Checkbox checked={on} onCheckedChange={() => { const next = new Set(picked); if (next.has(idx)) next.delete(idx); else next.add(idx); pick(next) }} />
                      </TableCell>
                    )}
                    <TableCell className='tabular-nums text-muted-foreground' title={`Population row ${idx + 1}`}>{rowCursor + position + 1}</TableCell>
                    {columns.map(({ key }) => {
                      const value = String(row[key] ?? '')
                      if (isChanges && rows.hiddenColumns.includes(key)) return <TableCell key={key} className='italic text-muted-foreground'>Hidden by client</TableCell>
                      return <TableCell key={key} className={key === 'title' && isChanges ? 'min-w-48 max-w-sm whitespace-normal' : 'max-w-[280px] truncate'}>
                        {isChanges && key === 'identifier' && row.url
                          ? <a href={String(row.url)} target='_blank' rel='noopener noreferrer' className='text-accent-foreground underline-offset-2 hover:underline'>{value || '—'}</a>
                          : (row[key] instanceof Date || /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) && !Number.isNaN(new Date(value).getTime())
                            ? fmt(value) : value || (isChanges ? '—' : '')}
                      </TableCell>
                    })}
                    {isChanges
                      ? <TableCell><PopulationEvidenceButton kind='change' row={row} snapshot={null} at={null} /></TableCell>
                      : rows.hiddenColumns.map((h) => <TableCell key={h} className='italic text-muted-foreground'>—</TableCell>)}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <div className='flex items-center justify-between gap-2 px-3 py-2'><Button size='sm' variant='outline' disabled={rowCursor === 0} onClick={() => setRowCursor(Math.max(0, rowCursor - 100))}>Previous rows</Button><span className='text-xs text-muted-foreground'>{rows.items.length ? rowCursor + 1 : 0}–{rowCursor + rows.items.length} of {rows.matchingCount ?? rows.totalCount} matching records</span><Button size='sm' variant='outline' disabled={rows.nextCursor == null} onClick={() => setRowCursor(rows.nextCursor!)}>Next rows</Button></div>
        </div>
      ) : null}

      <div>
        <h2 className='mb-2 text-sm font-semibold'>Sampling rounds</h2>
        {roundsQuery.data?.items.length ? (
          <ul className='divide-y rounded-md border bg-card text-sm'>
            {roundsQuery.data.items.map((r) => (
              <li key={r.id}>
                <Link to={ROUND_ROUTE} params={{ workspaceId, auditCycleId, populationId: population.id, sampleId: r.id }} className='group flex cursor-pointer flex-wrap items-center justify-between gap-2 px-4 py-2 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'>
                  <span><span className='font-medium'>Round {r.round}</span> · {r.method}{r.seed != null ? ` · seed ${r.seed}` : ''} · {r.sampleSize} items</span>
                  <span className='flex items-center gap-4 text-xs'>
                    <span className='text-muted-foreground'>{fmtTime(r.selectedAt)}</span>
                    <span className='inline-flex items-center gap-1.5 whitespace-nowrap font-medium text-cobalt-strong group-hover:underline'>Open round <ArrowRight className='size-3.5' aria-hidden='true' /></span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className='text-xs text-muted-foreground'>No rounds yet for this population.</p>
        )}
      </div>
    </div>
  )
}

// ── 3. Round ─────────────────────────────────────────────────────────

/** A round evidences tests: pick which, one generated submission each. */
function ApplyRoundToTests({ auditCycleId, populationId, sampleId, preselect, applying, applicationError }: {
  auditCycleId: string
  populationId: string
  sampleId: string
  preselect?: string
  applying: boolean
  applicationError?: string
}) {
  const queryClient = useQueryClient()
  const invalidateSamplingEvidence = useInvalidateSamplingEvidence()
  const testsQuery = useQuery(trpc.auditor.listSamplingTests.queryOptions({ auditCycleId, populationId }))
  const [selected, setSelected] = useState<Set<string>>(() => new Set(preselect ? [preselect] : []))
  const apply = useMutation({
    ...trpc.auditor.applyRoundToTests.mutationOptions(),
    onSuccess: async (results) => {
      toast.success(applySummary(results))
      if (results !== null) setSelected(new Set())
      invalidateSamplingEvidence()
      await queryClient.invalidateQueries({ queryKey: trpc.auditor.getSamplingRound.queryKey({ auditCycleId, sampleId }) })
    },
    onError: (error) => toast.error(error.message),
  })
  useEffect(() => {
    const tests = testsQuery.data
    if (!tests) return
    setSelected((previous) => new Set([...previous].filter((id) => !tests.some((test) => test.statusId === id && test.appliedSampleIds.includes(sampleId)))))
  }, [testsQuery.data, sampleId])
  const busy = apply.isPending || applying
  const tests = testsQuery.data ?? []
  // Selected minus already-applied (a preselected test may be applied already).
  const pending = tests.filter((t) => selected.has(t.statusId) && !t.appliedSampleIds.includes(sampleId)).map((t) => t.statusId)
  if (testsQuery.isPending) return null
  return (
    <section>
      <h2 className='mb-1 text-sm font-semibold'>Apply to tests</h2>
      {tests.length === 0 ? (
        <p className='text-xs text-muted-foreground'>No test in this cycle is evidenced by sampling this population.</p>
      ) : (
        <>
          <p className='mb-2 text-xs text-muted-foreground'>
            Each selected test gets this round as its evidence: one submission per test, reading every item against the test's criterion.
            The client's approver then approves it or requests changes; nothing is approved automatically.
          </p>
          <ul className='divide-y rounded-md border bg-card text-sm'>
            {tests.map((t) => {
              const applied = t.appliedSampleIds.includes(sampleId)
              return (
                <li key={t.statusId} className='flex items-center gap-3 px-4 py-2'>
                  <Checkbox
                    aria-label={`Apply to ${t.code ?? t.title}`}
                    checked={applied || selected.has(t.statusId)}
                    disabled={applied || busy}
                    onCheckedChange={() => setSelected((prev) => {
                      const next = new Set(prev)
                      if (next.has(t.statusId)) next.delete(t.statusId); else next.add(t.statusId)
                      return next
                    })}
                  />
                  <div className='min-w-0 flex-1'>
                    <div className='truncate font-medium'>{t.code} {t.title}</div>
                    <div className='text-xs text-muted-foreground'>{t.facetLabel}</div>
                    {t.controls?.length > 0 && <div className='mt-1 flex flex-wrap gap-1.5'>
                      {t.controls.map((control) => <Badge key={control.id} variant='outline' className='whitespace-normal text-xs'>
                        {control.code && `${control.code} `}{control.title}
                      </Badge>)}
                    </div>}
                  </div>
                  {applied && <Badge variant='outline' className='text-xs'>applied</Badge>}
                </li>
              )
            })}
          </ul>
          <Button size='sm' className='mt-2' aria-busy={busy} disabled={pending.length === 0 || busy}
            onClick={() => apply.mutate({ auditCycleId, sampleId, statusIds: pending })}>
            {busy && <Loader2 className='animate-spin' />}
            {applying ? 'Applying…' : apply.isPending ? 'Submitting…' : `Apply to ${pending.length} ${pending.length === 1 ? 'test' : 'tests'}`}
          </Button>
          {busy && <p role='status' className='mt-2 text-xs text-muted-foreground'>{applying ? 'Collecting sample evidence and preparing submissions…' : 'Submitting application…'}</p>}
          {!busy && applicationError && <p role='alert' className='mt-2 text-xs text-destructive'>Application failed: {applicationError} Select the tests and apply again to retry.</p>}
        </>
      )}
    </section>
  )
}

export function AuditorSamplingRoundPage() {
  const { workspaceId, auditCycleId, populationId, sampleId, forTest } = useCycleParams()
  const invalidateSamplingEvidence = useInvalidateSamplingEvidence()
  const roundQuery = useQuery({
    ...trpc.auditor.getSamplingRound.queryOptions({ auditCycleId, sampleId: sampleId! }),
    refetchInterval: (query) => query.state.data?.application?.state === 'collecting' ? 5000 : false,
  })
  useEffect(() => {
    if (roundQuery.data && roundQuery.data.application?.state !== 'collecting') invalidateSamplingEvidence()
  }, [roundQuery.data, invalidateSamplingEvidence])
  const listQuery = useQuery(trpc.auditor.listCyclePopulations.queryOptions({ auditCycleId }))
  if (roundQuery.isPending) return <div className='text-sm text-muted-foreground'>Loading round…</div>
  const round = roundQuery.data
  if (!round) return <AuditorInlineEmptyState message='Round not found.' />
  const cls = listQuery.data?.items.find((p) => p.id === populationId)?.class ?? 'population'
  const downloadCsv = () => {
    const filename = [cls, `round-${round.round}`, round.method.toLowerCase(), round.seed != null ? `seed-${round.seed}` : null].filter(Boolean).join('-') + '.csv'
    downloadBase64File(btoa(unescape(encodeURIComponent(roundCsv(round.items)))), filename, 'text/csv')
  }
  return (
    <div className='space-y-3'>
      <Link to={DETAIL_ROUTE} params={{ workspaceId, auditCycleId, populationId: populationId! }} className='inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'>
        <ArrowLeft className='h-3.5 w-3.5' /> Population · re-sample
      </Link>
      <div className='flex flex-wrap items-start justify-between gap-2'>
        <div>
          <h1 className='text-xl font-semibold'>Round {round.round}</h1>
          <p className='text-xs text-muted-foreground'>
            {round.method}{round.seed != null ? ` · seed ${round.seed}` : ''} · {round.sampleSize} items · selected by {round.selectedByName ?? round.selectedBy} · {fmtTime(round.selectedAt)}
          </p>
          {round.notes && <p className='mt-1 text-xs italic text-muted-foreground'>“{round.notes}”</p>}
          <p className='mt-1 text-xs text-muted-foreground'>Risk facts come from the population’s original freeze, not selection time. People and device evidence is saved at selection. Applying a Changes round collects its evidence in the background, then submits it to the selected tests. Viewing never changes evidence.</p>
        </div>
        <Button size='sm' variant='outline' onClick={downloadCsv}>
          <Download className='mr-1 h-3.5 w-3.5' /> CSV
        </Button>
      </div>
      {cls === 'changes' && <p className='text-sm text-muted-foreground'>Evidence collected: {round.items.filter((item) => item.evidenceSnapshotAt).length}/{round.items.length}. Apply this round to collect any missing evidence and create the submissions.</p>}
      <div className='overflow-x-auto rounded-md border bg-card'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-12'>#</TableHead>
              <TableHead>Item</TableHead>
              <TableHead>Evidence snapshot</TableHead>
              <TableHead>Snapshot at</TableHead>
              {(cls === 'changes' || cls === 'risks') && <TableHead>Details</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {round.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className='tabular-nums text-muted-foreground'>{item.rowIndex + 1}</TableCell>
                <TableCell>
                  <div className='font-medium'>{item.identifier}</div>
                  {item.isReplacement && <Badge variant='outline' className='text-xs'>replacement</Badge>}
                </TableCell>
                <TableCell><SnapshotChips snapshot={item.evidenceSnapshot} /></TableCell>
                <TableCell className='text-xs text-muted-foreground'>{fmtTime(item.evidenceSnapshotAt)}</TableCell>
                {cls === 'changes' && <TableCell><PopulationEvidenceButton kind='change' row={item.rowData} snapshot={item.evidenceSnapshot} at={item.evidenceSnapshotAt} /></TableCell>}
                {cls === 'risks' && <TableCell><PopulationEvidenceButton kind='risk' row={item.rowData} snapshot={item.evidenceSnapshot} at={item.evidenceSnapshotAt} /></TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {round.samplingScope && <div className='rounded-md border bg-card px-4 py-3 text-xs'>
        <p className='font-semibold'>Saved sampling scope</p>
        <p>Full population: {round.samplingScope.populationCount} · Eligible: {round.samplingScope.eligibleIndices.length} · Selected: {round.sampleSize}</p>
        {round.samplingScope.filters.map((rule, i) => <p key={i}>{rule.field} {rule.operator.replaceAll('_', ' ')} {String(rule.value)}</p>)}
      </div>}

      <ApplyRoundToTests auditCycleId={auditCycleId} populationId={populationId!} sampleId={round.id} preselect={forTest} applying={round.application?.state === 'collecting'} applicationError={round.application?.state === 'failed' ? round.application.error : undefined} />
    </div>
  )
}
